import type { GridsterEngine } from './gridster-engine';
import type { GridsterItemInstance } from './gridster-item-instance';
import { GridsterPush, PUSH_DIRECTION } from './gridster-push';
import { cancelScroll, scroll } from './gridster-scroll';
import { GridsterSwap } from './gridster-swap';
import { checkDragHandleClass } from './gridster-utils';

const RESIZABLE_HANDLER_SELECTOR = '.gridster-item-resizable-handler';

export class GridsterDraggable {
  private enabled = false;
  private pointerId: number | null = null;
  private delayTimeout: number | null = null;
  private delayEvent: PointerEvent | null = null;
  private push: GridsterPush | null = null;
  private swap: GridsterSwap | null = null;
  private path: { x: number; y: number }[] = [];
  private collision: GridsterItemInstance | true | false = false;
  private diffLeft = 0;
  private diffTop = 0;
  private itemWidth = 0;
  private itemHeight = 0;
  private left = 0;
  private top = 0;
  private pointer = { clientX: 0, clientY: 0 };

  constructor(
    private item: GridsterItemInstance,
    private gridster: GridsterEngine
  ) {}

  toggle(): void {
    const enableDrag = this.item.canBeDragged();
    if (!this.enabled && enableDrag) {
      this.enabled = true;
      this.item.el.addEventListener('pointerdown', this.dragStartDelay);
    } else if (this.enabled && !enableDrag) {
      this.enabled = false;
      this.item.el.removeEventListener('pointerdown', this.dragStartDelay);
    }
  }

  destroy(): void {
    this.toggle();
    this.clearDelay();
    if (this.item.isMoving) {
      this.stop();
    }
    this.push = null;
    this.swap = null;
  }

  private dragStartDelay = (e: PointerEvent): void => {
    if (this.gridster.dragInProgress) {
      return;
    }
    if (e.pointerType === 'mouse' && e.button !== 0) {
      return;
    }
    const target = e.target as HTMLElement;
    if (target.closest(RESIZABLE_HANDLER_SELECTOR)) {
      return;
    }
    const { dragHandleClass, ignoreContentClass, delayStart } = this.gridster.options.draggable;
    if (!checkDragHandleClass(target, this.item.el, dragHandleClass, ignoreContentClass)) {
      return;
    }
    if (!delayStart) {
      this.dragStart(e);
      return;
    }

    this.delayEvent = e;
    this.delayTimeout = window.setTimeout(this.startDelayedDrag, delayStart);
    document.addEventListener('pointermove', this.cancelDelayOnMove);
    document.addEventListener('pointerup', this.cancelDelay);
    document.addEventListener('pointercancel', this.cancelDelay);
    window.addEventListener('blur', this.cancelDelay);
  };

  private startDelayedDrag = (): void => {
    const event = this.delayEvent;
    this.clearDelay();
    if (event) {
      this.dragStart(event);
    }
  };

  private cancelDelay = (): void => {
    this.clearDelay();
  };

  private cancelOnBlur = (): void => {
    this.stop();
  };

  private cancelDelayOnMove = (e: PointerEvent): void => {
    const start = this.delayEvent;
    if (!start) {
      return;
    }
    if (Math.abs(e.clientX - start.clientX) > 9 || Math.abs(e.clientY - start.clientY) > 9) {
      this.clearDelay();
    }
  };

  private clearDelay(): void {
    if (this.delayTimeout !== null) {
      window.clearTimeout(this.delayTimeout);
      this.delayTimeout = null;
    }
    this.delayEvent = null;
    document.removeEventListener('pointermove', this.cancelDelayOnMove);
    document.removeEventListener('pointerup', this.cancelDelay);
    document.removeEventListener('pointercancel', this.cancelDelay);
    window.removeEventListener('blur', this.cancelDelay);
  }

  private dragStart(e: PointerEvent): void {
    this.clearDelay();
    if (this.gridster.dragInProgress || !this.gridster.el) {
      return;
    }

    e.stopPropagation();
    e.preventDefault();

    const el = this.item.el;
    this.pointerId = e.pointerId;
    el.setPointerCapture(e.pointerId);
    document.addEventListener('pointermove', this.dragMove);
    document.addEventListener('pointerup', this.dragStop);
    document.addEventListener('pointercancel', this.dragStop);
    window.addEventListener('blur', this.cancelOnBlur);

    el.classList.add('gridster-item-moving');
    this.item.isMoving = true;

    const rect = this.gridster.el.getBoundingClientRect();
    this.diffLeft = e.clientX - rect.left - this.item.left;
    this.diffTop = e.clientY - rect.top - this.item.top;
    this.itemWidth = this.item.width;
    this.itemHeight = this.item.height;
    this.pointer = { clientX: e.clientX, clientY: e.clientY };

    this.gridster.movingItem = this.item.$item;
    this.gridster.previewStyle();
    this.push = new GridsterPush(this.item, this.gridster);
    this.swap = new GridsterSwap(this.item, this.gridster);
    this.gridster.dragInProgress = true;
    this.collision = false;
    this.path = [{ x: this.item.item.x || 0, y: this.item.item.y || 0 }];
  }

  private dragMove = (e: PointerEvent): void => {
    if (!this.push || !this.swap) {
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    this.pointer = { clientX: e.clientX, clientY: e.clientY };
    this.calculateItemPosition();
  };

  private calculateItemPosition = (): void => {
    const el = this.gridster.el;
    if (!el || !this.push || !this.swap) {
      return;
    }

    const rect = el.getBoundingClientRect();
    this.left = this.pointer.clientX - rect.left - this.diffLeft;
    this.top = this.pointer.clientY - rect.top - this.diffTop;

    scroll(
      this.gridster,
      { left: this.left, top: this.top, width: this.itemWidth, height: this.itemHeight },
      this.calculateItemPosition
    );

    const $item = (this.gridster.movingItem = this.item.$item);
    const positionX = this.gridster.pixelsToPositionX(this.left, Math.round);
    const positionY = this.gridster.pixelsToPositionY(this.top, Math.round);
    const positionXBackup = $item.x;
    const positionYBackup = $item.y;

    $item.x = positionX;
    if (this.gridster.checkGridCollision($item)) {
      $item.x = positionXBackup;
    }
    $item.y = positionY;
    if (this.gridster.checkGridCollision($item)) {
      $item.y = positionYBackup;
    }

    this.gridster.gridRenderer.setCellPosition(this.item.el, this.left, this.top);

    if (positionXBackup !== $item.x || positionYBackup !== $item.y) {
      const lastPosition = this.path[this.path.length - 1];
      let direction = '';
      if (lastPosition.x < $item.x) {
        direction = PUSH_DIRECTION.fromWest;
      } else if (lastPosition.x > $item.x) {
        direction = PUSH_DIRECTION.fromEast;
      } else if (lastPosition.y < $item.y) {
        direction = PUSH_DIRECTION.fromNorth;
      } else if (lastPosition.y > $item.y) {
        direction = PUSH_DIRECTION.fromSouth;
      }

      this.push.pushItems(direction, this.gridster.options.disablePushOnDrag);
      this.swap.swapItems();
      this.collision = this.gridster.checkCollision($item);
      if (this.collision) {
        $item.x = positionXBackup;
        $item.y = positionYBackup;
      } else {
        this.path.push({ x: $item.x, y: $item.y });
      }
      this.push.checkPushBack();
    } else {
      this.collision = false;
    }

    this.gridster.previewStyle();
  };

  private dragStop = (e: PointerEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    this.stop();
  };

  private stop(): void {
    cancelScroll();
    document.removeEventListener('pointermove', this.dragMove);
    document.removeEventListener('pointerup', this.dragStop);
    document.removeEventListener('pointercancel', this.dragStop);
    window.removeEventListener('blur', this.cancelOnBlur);

    const el = this.item.el;
    if (this.pointerId !== null && el.hasPointerCapture?.(this.pointerId)) {
      el.releasePointerCapture(this.pointerId);
    }
    this.pointerId = null;

    el.classList.remove('gridster-item-moving');
    this.item.isMoving = false;
    this.gridster.dragInProgress = false;
    this.path = [];
    this.collision = false;

    this.item.setSize();
    this.item.checkItemChanges(this.item.$item, this.item.item);
    this.push?.setPushedItems();
    this.swap?.setSwapItem();
    this.push = null;
    this.swap = null;
    this.gridster.flushItemChanges();

    this.gridster.movingItem = null;
    this.gridster.previewStyle();
  }
}
